// Terms of Service & Acceptable Use Policy.
//
// Reviewed and approved 2026-07-15. Encodes the compliance obligations the
// product depends on (it shifts lawful-use responsibility to the customer).
// Keep the TERMS_VERSION in backend user_service.py in sync when the substance
// changes (bump it to force re-acceptance).

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { theme } from './theme';
import { setPageSeo } from './seo';

const S = {
  page: { background: theme.bg, minHeight: '100vh', color: theme.text,
    fontFamily: 'system-ui, -apple-system, sans-serif' },
  wrap: { maxWidth: 760, margin: '0 auto', padding: '32px 22px 80px' },
  brand: { color: theme.primary, fontWeight: 800, letterSpacing: 2, fontSize: '0.85rem' },
  h1: { color: theme.heading, fontSize: '1.9rem', margin: '10px 0 4px', letterSpacing: '-0.5px' },
  meta: { color: theme.textMute, fontSize: '0.82rem', margin: 0 },
  h2: { color: theme.heading, fontSize: '1.15rem', margin: '30px 0 8px' },
  p: { fontSize: '0.95rem', lineHeight: 1.65, margin: '0 0 12px' },
  li: { fontSize: '0.95rem', lineHeight: 1.6, marginBottom: 8 },
  a: { color: theme.primary },
};

export default function TermsPage() {
  // Sets the canonical too, not just the title: setting only document.title
  // left the index.html default in place, so this page told Google it was a
  // duplicate of the homepage and was filed under "Alternate page with proper
  // canonical tag" rather than indexed on its own.
  useEffect(() => {
    setPageSeo({
      title: 'Terms & Acceptable Use — Amanar Lead Engine',
      description: 'Terms of Service and Acceptable Use Policy for Amanar Lead Engine — '
        + 'how Florida business-licensing lead data may be used, and the obligations '
        + 'that come with it.',
      canonical: 'https://leadengine.amanar.ai/terms',
      robots: 'index, follow',
    });
  }, []);
  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <Link to="/" style={S.brand}>AMANAR LEAD ENGINE</Link>
        <h1 style={S.h1}>Terms of Service &amp; Acceptable Use Policy</h1>
        <p style={S.meta}>Version 2026-07-14 · Effective 2026-07-15</p>

        <h2 style={S.h2}>1. What the service provides</h2>
        <p style={S.p}>
          Amanar Lead Engine (&ldquo;the Service&rdquo;) compiles business information
          from Florida public records — including the Department of Business and
          Professional Regulation (DBPR) and the Division of Corporations
          (Sunbiz) — together with information researched from public sources and
          information submitted directly by business owners. We provide this
          information as a data tool. We do not place calls, send messages, or
          conduct outreach on your behalf.
        </p>

        <h2 style={S.h2}>2. No warranty on accuracy or eligibility</h2>
        <p style={S.p}>
          Public records may be incomplete, delayed, or contain errors. A business
          appearing in the Service is not certified by us as licensed, operating,
          or eligible to be contacted. You are responsible for verifying any
          information before you rely on it.
        </p>

        <h2 style={S.h2}>3. Your compliance responsibilities</h2>
        <p style={S.p}>
          You are solely responsible for ensuring that your use of the data
          complies with all applicable laws. You specifically agree that:
        </p>
        <ul>
          <li style={S.li}>
            <strong>Telephone &amp; text (TCPA / TSR).</strong> You will not use an
            automatic telephone dialing system, prerecorded message, or SMS/text to
            contact any mobile number without the prior express consent required by
            law. Many owner numbers in public records are personal mobile numbers.
            You will honor the National and any state Do-Not-Call registries and
            internal do-not-call requests, and you will maintain the records the FTC
            Telemarketing Sales Rule requires.
          </li>
          <li style={S.li}>
            <strong>Email (CAN-SPAM).</strong> Any commercial email you send will use
            accurate headers and subject lines, identify itself as an advertisement
            where required, include a valid physical postal address, and provide a
            working opt-out that you honor promptly.
          </li>
          <li style={S.li}>
            <strong>State privacy laws.</strong> You will comply with applicable
            state privacy laws (including, where relevant, the CCPA/CPRA as it
            applies to business contacts) and honor opt-out and deletion requests.
          </li>
          <li style={S.li}>
            <strong>Consent status.</strong> Exports label each record&rsquo;s contact
            basis. Only records marked &ldquo;Consented (opted-in)&rdquo; reflect a
            person who agreed to be contacted by vendors. Records marked &ldquo;Public
            record&rdquo; carry no such consent, and you must apply the appropriate
            restrictions before contacting them.
          </li>
        </ul>

        <h2 style={S.h2}>4. Prohibited uses</h2>
        <p style={S.p}>You will not use the Service to:</p>
        <ul>
          <li style={S.li}>Resell, redistribute, or sublicense the raw data as a competing list product.</li>
          <li style={S.li}>Contact individuals or businesses who have asked not to be contacted.</li>
          <li style={S.li}>Engage in harassment, fraud, deceptive marketing, or any unlawful activity.</li>
          <li style={S.li}>Attempt to identify or target individuals whose information is legally exempt or protected.</li>
          <li style={S.li}>Scrape, bulk-download, or circumvent access controls beyond your plan&rsquo;s limits.</li>
        </ul>

        <h2 style={S.h2}>5. Do-not-contact requests</h2>
        <p style={S.p}>
          If a business asks to be removed, we will suppress it from the Service on a
          reasonable timeline, and you agree to stop contacting anyone who has asked
          you to stop. Removal requests can be sent to our support contact.
        </p>

        <h2 style={S.h2}>6. Indemnification &amp; limitation of liability</h2>
        <p style={S.p}>
          You agree to indemnify Amanar against claims arising from your use of the
          data, including outreach that violates the laws referenced above. The
          Service is provided &ldquo;as is,&rdquo; without warranties, to the maximum
          extent permitted by law.
        </p>

        <h2 style={S.h2}>7. Changes</h2>
        <p style={S.p}>
          We may update these terms; continued use after an update constitutes
          acceptance. Material changes may require you to re-accept.
        </p>

        <p style={{ ...S.p, marginTop: 28, color: theme.textMute }}>
          Questions? Contact your Amanar representative. &nbsp;
          <Link to="/" style={S.a}>← Back to home</Link>
        </p>
      </div>
    </div>
  );
}
